// Lightweight canvas recording helper using MediaRecorder
// - Tries video/mp4 first, falls back to webm
// - Optionally upscales via an offscreen canvas to produce higher-resolution clips
// - Returns { stop, finalize } and calls onStop({ blob, mime, ext }) when finished

export function startCanvasRecording({ canvas, fps = 60, preferMp4 = false, onStop, onData = null, timesliceMs = 500, scale = 1, videoBitsPerSecond = 4000000, width = null, height = null, fit = 'cover', cropBiasX = 0, cropBiasY = 0, getCropBiasX = null, getCropBiasY = null }) {
  if (!canvas || !canvas.captureStream) {
    throw new Error('Recording failed: canvas.captureStream() not available')
  }

  let srcCanvas = canvas
  let offscreen = null
  let rafId = null
  const isHeadless = typeof window !== 'undefined' && window.__HRA_HEADLESS_MODE__ === true

  const resolveCanvasDimensions = (node) => {
    if (!node) return { width: 960, height: 540 }
    const attrWidth = Number(node.width || (typeof node.getAttribute === 'function' ? node.getAttribute('width') : 0)) || 0
    const attrHeight = Number(node.height || (typeof node.getAttribute === 'function' ? node.getAttribute('height') : 0)) || 0
    let rectWidth = 0
    let rectHeight = 0
    try {
      if (typeof node.getBoundingClientRect === 'function') {
        const rect = node.getBoundingClientRect()
        rectWidth = Math.round(rect?.width || 0)
        rectHeight = Math.round(rect?.height || 0)
      }
    } catch (_) {}
    const widthCandidate = Math.max(attrWidth, rectWidth, 960)
    const heightCandidate = Math.max(attrHeight, rectHeight, 540)
    return {
      width: Math.max(2, widthCandidate),
      height: Math.max(2, heightCandidate)
    }
  }

  const baseSize = resolveCanvasDimensions(canvas)

  const wantsFixedSize = Number(width) > 0 && Number(height) > 0
  const scaleFactor = Number(scale) > 0 ? Number(scale) : 1
  const forceCopyStream = isHeadless || Number(scale) > 1 || wantsFixedSize
  let headlessDebugLogged = false
  let headlessFrameCount = 0

  if (forceCopyStream) {
    const fixedWidth = Math.round(Number(width))
    const fixedHeight = Math.round(Number(height))
    const targetWidth = wantsFixedSize
      ? Math.max(2, Number.isFinite(fixedWidth) && fixedWidth > 0 ? fixedWidth : baseSize.width)
      : Math.max(2, Math.round(baseSize.width * scaleFactor))
    const targetHeight = wantsFixedSize
      ? Math.max(2, Number.isFinite(fixedHeight) && fixedHeight > 0 ? fixedHeight : baseSize.height)
      : Math.max(2, Math.round(baseSize.height * scaleFactor))
    offscreen = document.createElement('canvas')
    offscreen.width = targetWidth
    offscreen.height = targetHeight
    const ctx = offscreen.getContext('2d', { alpha: true })
    if (isHeadless && !headlessDebugLogged) {
      headlessDebugLogged = true
      try {
        console.log('Headless recorder init', {
          srcWidth: canvas.width,
          srcHeight: canvas.height,
          targetWidth,
          targetHeight,
          wantsFixedSize,
          scaleFactor
        })
      } catch (_) {}
    }
    const draw = () => {
      try {
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.clearRect(0, 0, targetWidth, targetHeight)
        const sw = Math.max(1, srcCanvas.width || baseSize.width)
        const sh = Math.max(1, srcCanvas.height || baseSize.height)
        let sx = 0, sy = 0, sW = sw, sH = sh, dX = 0, dY = 0, dW = targetWidth, dH = targetHeight
        if (fit === 'cover') {
          const scaleCover = Math.max(targetWidth / sw, targetHeight / sh)
          sW = Math.min(sw, Math.round(targetWidth / Math.max(scaleCover, 1)))
          sH = Math.min(sh, Math.round(targetHeight / Math.max(scaleCover, 1)))
          sx = Math.floor((sw - sW) / 2)
          sy = Math.floor((sh - sH) / 2)
          let bx = 0, by = 0
          try {
            if (typeof getCropBiasX === 'function') bx = Number(getCropBiasX()) || 0
            else if (typeof cropBiasX === 'number') bx = Number(cropBiasX) || 0
            else if (typeof window !== 'undefined' && typeof window.__recordingCropBiasX === 'number') bx = Number(window.__recordingCropBiasX) || 0
          } catch (_) {}
          try {
            if (typeof getCropBiasY === 'function') by = Number(getCropBiasY()) || 0
            else if (typeof cropBiasY === 'number') by = Number(cropBiasY) || 0
            else if (typeof window !== 'undefined' && typeof window.__recordingCropBiasY === 'number') by = Number(window.__recordingCropBiasY) || 0
          } catch (_) {}
          if (!Number.isFinite(bx)) bx = 0
          if (!Number.isFinite(by)) by = 0
          bx = Math.max(-1, Math.min(1, bx))
          by = Math.max(-1, Math.min(1, by))
          const maxShiftX = Math.max(0, sw - sW)
          const maxShiftY = Math.max(0, sh - sH)
          sx = Math.round((bx * 0.5 + 0.5) * maxShiftX)
          sy = Math.round((by * 0.5 + 0.5) * maxShiftY)
        } else if (fit === 'contain') {
          const scaleContain = Math.min(targetWidth / sw, targetHeight / sh)
          dW = Math.round(sw * scaleContain)
          dH = Math.round(sh * scaleContain)
          dX = Math.floor((targetWidth - dW) / 2)
          dY = Math.floor((targetHeight - dH) / 2)
          sW = sw; sH = sh; sx = 0; sy = 0
        }
        ctx.drawImage(srcCanvas, sx, sy, sW, sH, dX, dY, dW, dH)
        if (isHeadless && headlessFrameCount < 5) {
          headlessFrameCount += 1
          try {
            console.log('Headless recorder frame', {
              frame: headlessFrameCount,
              sw,
              sh,
              sx,
              sy,
              sW,
              sH,
              dW,
              dH
            })
          } catch (_) {}
        }
      } catch (_) {}
      rafId = requestAnimationFrame(draw)
    }
    rafId = requestAnimationFrame(draw)
  }

  const stream = (offscreen || srcCanvas).captureStream(fps)
  // Keep frames flowing on browsers that require explicit frame requests
  const vtrack = stream.getVideoTracks && stream.getVideoTracks()[0]
  let frameTimer = null
  if (vtrack && typeof vtrack.requestFrame === 'function') {
    const interval = Math.max(1000 / (fps || 60), 200)
    frameTimer = setInterval(() => { try { vtrack.requestFrame() } catch (_) {} }, interval)
  }
  const mimeCandidates = []
  if (preferMp4) {
    mimeCandidates.push('video/mp4;codecs=h264')
    mimeCandidates.push('video/mp4')
  }
  // Prefer VP8 for broader compatibility, keep VP9 as secondary
  mimeCandidates.push('video/webm;codecs=vp8,opus')
  mimeCandidates.push('video/webm;codecs=vp9,opus')
  mimeCandidates.push('video/webm;codecs=vp8')
  mimeCandidates.push('video/webm;codecs=vp9')
  mimeCandidates.push('video/webm')

  let mime = ''
  for (const m of mimeCandidates) {
    if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) {
      mime = m
      break
    }
  }
  if (!mime) {
    // As a last resort, try default
    mime = ''
  }

  const chunks = []
  let finalized = false
  let finalizeTimer = null
  let finalizeDeadline = null
  let recOpts = {}
  if (mime) recOpts.mimeType = mime
  if (Number(videoBitsPerSecond) > 0) recOpts.videoBitsPerSecond = Number(videoBitsPerSecond)
  const recorder = new MediaRecorder(stream, Object.keys(recOpts).length ? recOpts : undefined)
  const finalizeRecording = () => {
    if (finalized) return
    finalized = true
    if (finalizeTimer) { try { clearTimeout(finalizeTimer) } catch (_) {} finalizeTimer = null }
    finalizeDeadline = null
    if (frameTimer) { try { clearInterval(frameTimer) } catch (_) {} frameTimer = null }
    if (rafId) { try { cancelAnimationFrame(rafId) } catch (_) {} rafId = null }
    const type = normalizeMime(recorder.mimeType || mime || 'video/webm')
    const blob = chunks.length ? new Blob(chunks, { type }) : new Blob([], { type })
    const isMp4 = type.includes('mp4')
    onStop && onStop({ blob, mime: type, ext: isMp4 ? 'mp4' : 'webm' })
  }

  const pollFinalize = () => {
    finalizeTimer = null
    if (finalized) return
    if (chunks.length) {
      finalizeRecording()
      return
    }
    if (finalizeDeadline && Date.now() >= finalizeDeadline) {
      finalizeRecording()
      return
    }
    finalizeTimer = setTimeout(pollFinalize, 150)
  }

  const requestAndScheduleFinalize = () => {
    if (finalized) return
    if (!chunks.length) {
      try {
        if (recorder.state !== 'inactive' && typeof recorder.requestData === 'function') recorder.requestData()
      } catch (_) {}
      if (!finalizeTimer) {
        finalizeDeadline = Date.now() + 2000
        finalizeTimer = setTimeout(pollFinalize, 150)
      }
      return
    }
    finalizeRecording()
  }

  recorder.ondataavailable = (e) => {
    if (e && e.data && e.data.size) {
      try { if (typeof onData === 'function') onData(e.data) } catch (_) {}
      chunks.push(e.data)
    }
    if (recorder.state === 'inactive') {
      requestAndScheduleFinalize()
    }
  }

  function normalizeMime(value) {
    if (typeof value === 'string') {
      const lower = value.toLowerCase()
      if (lower.startsWith('video/webm')) return 'video/webm'
      if (lower.startsWith('video/mp4')) return 'video/mp4'
    }
    return value || 'video/webm'
  }

  recorder.onstop = () => {
    requestAndScheduleFinalize()
  }
  try {
    recorder.start(Math.max(0, Number(timesliceMs) || 0))
  } catch {
    recorder.start()
  }

  return {
    stop: () => {
      if (finalized) return
      try { if (recorder.state !== 'inactive' && typeof recorder.requestData === 'function') recorder.requestData() } catch (_) {}
      try { recorder.stop() } catch (_) {}
    },
    finalize: () => {
      if (finalized) return
      try { if (recorder.state !== 'inactive' && typeof recorder.requestData === 'function') recorder.requestData() } catch (_) {}
      try { if (recorder.state !== 'inactive') recorder.stop() } catch (_) {}
      requestAndScheduleFinalize()
    }
  }
}

export async function saveRecording({ blob, filename, toServer = false, serverUrl = '' }) {
  if (!toServer) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    return { ok: true, saved: 'download' }
  }

  const base = serverUrl || ''
  const target = base.replace(/\/$/, '') + '/save-video?filename=' + encodeURIComponent(filename)
  const res = await fetch(target, { method: 'POST', body: blob })
  if (!res.ok) throw new Error('Server save failed: ' + res.status)
  return { ok: true, saved: 'server' }
}

let ffmpegLoaderPromise = null;

async function ensureFFmpeg() {
  if (!ffmpegLoaderPromise) {
    ffmpegLoaderPromise = import('@ffmpeg/ffmpeg').then(async ({ createFFmpeg, fetchFile }) => {
      const ffmpeg = createFFmpeg({ log: false });
      await ffmpeg.load();
      return { ffmpeg, fetchFile };
    }).catch((err) => {
      ffmpegLoaderPromise = null;
      throw err;
    });
  }
  return ffmpegLoaderPromise;
}

export async function transcodeToMp4({ webmBlob, fps = 30, signal = null } = {}) {
  if (!webmBlob || !webmBlob.size) {
    return { ok: false, error: 'Missing input blob', fallbackBlob: webmBlob };
  }
  try {
    const { ffmpeg, fetchFile } = await ensureFFmpeg();
    const inputName = `input_${Date.now()}.webm`;
    const outputName = `output_${Date.now()}.mp4`;
    ffmpeg.FS('writeFile', inputName, await fetchFile(webmBlob));
    const args = ['-i', inputName, '-movflags', 'faststart', '-pix_fmt', 'yuv420p'];
    if (fps && Number(fps) > 0) {
      args.push('-r', String(Math.max(1, Math.round(Number(fps)))));
    }
    args.push('-c:v', 'libx264', '-preset', 'fast', '-crf', '28', '-c:a', 'aac', '-b:a', '128k', outputName);
    await ffmpeg.run(...args);
    const data = ffmpeg.FS('readFile', outputName);
    try { ffmpeg.FS('unlink', inputName); } catch (_) {}
    try { ffmpeg.FS('unlink', outputName); } catch (_) {}
    const mp4Blob = new Blob([data.buffer], { type: 'video/mp4' });
    return { ok: true, blob: mp4Blob };
  } catch (err) {
    console.warn('transcodeToMp4 failed', err);
    return { ok: false, error: err?.message || 'Transcode failed', fallbackBlob: webmBlob };
  }
}
